import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

// Types
import { IntegrationSources } from '../types';

// Hooks
import { useToggle } from 'Shared/utils/hooks';

// Actions
import { fetchJiraProjectEpics } from 'Shared/actions/UpdateAis.actions';

// Components
import WpSelectV2, { WidthType } from 'Wpds/components/select/WpSelectV2';
import { WpModal, WpModalBody, WpModalHeader, WpModalFooter } from 'Wpds/components/modal';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import IntegrationsWrapper from 'Dashboard/components/IntegrationsWrapper/IntegrationsWrapper';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpBadge } from 'Wpds/components/badge';

// Styles
import './JiraSourceModal.scss';

type Props = {
  isOpen: boolean;
  toggleIsOpen: () => void;
  selectedEpics;
  handleOnChange: Function;
  oauthRedirectUrl?: string;
};

const JiraSourceModal = ({ isOpen, toggleIsOpen, selectedEpics = [], handleOnChange, oauthRedirectUrl }: Props) => {
  const [projects, setProjects] = useState(null);
  const [loadingProjects, toggleLoadingProjects] = useToggle(true);

  const dataSource = useSelector(state => state.data_sources.find(src => src.slug === IntegrationSources.JIRA));
  const { connected } = dataSource;

  useEffect(() => {
    if (connected)
      fetchJiraProjectEpics().then(response => {
        setProjects(response.data);
        toggleLoadingProjects();
      });
  }, []);

  const handleUpdate = option => {
    const hasAready = selectedEpics.find(e => e.epic_id === option.epic_id);

    if (hasAready) {
      handleOnChange(selectedEpics.filter(e => e.epic_id !== option.epic_id));
    } else {
      handleOnChange([...selectedEpics, { epic_id: option.epic_id, project_id: option.project_id }]);
    }
  };

  const baseClassName = 'JiraSourceModal';

  const CustomInnerDropdown = ({ name, id }) => {
    const count = selectedEpics.filter(e => e.project_id === id).length;
    return (
      <WpContainer className={`${baseClassName}__project-container`}>
        <WpContainerBody padding="05" className={`${baseClassName}__project-container__body`}>
          <div>
            [{id}] {name}
          </div>
          <div className={`${baseClassName}__project-container__body__details`}>
            <div className={`${baseClassName}__project-container__body__details__count`}>
              <WpBadge color={count > 0 ? 'green' : 'gray'}>{count} selected</WpBadge>
            </div>
            <WpIcon name="angle-right" />
          </div>
        </WpContainerBody>
      </WpContainer>
    );
  };

  const CustomOption = ({ option }) => {
    return (
      <div className={`${baseClassName}__option`}>
        <WpCheckbox
          id={`department-filter-option-cb-${option.value}`}
          label={option.label}
          checked={selectedEpics.find(e => e.epic_id === option.epic_id)}
        />
      </div>
    );
  };

  return (
    <WpModal isOpen={isOpen} onToggle={toggleIsOpen} innerClassName={baseClassName} size="sm" dismissOnBackdrop={false}>
      <WpModalHeader>Jira</WpModalHeader>
      <WpModalBody>
        {connected ? (
          <>
            {loadingProjects ? (
              <>loading jira projects...</>
            ) : (
              <>
                <p>Select the Epics for each project to be used in your update.</p>
                {projects.map(project => (
                  <WpSelectV2
                    key={`${project.project_id}-project-filter`}
                    id={`${project.project_id}-project-filter`}
                    label={project.project_name}
                    // labelHidden
                    optionsWidth={WidthType.target}
                    options={project.epics.map(epic => ({
                      ...epic,
                      project_id: project.project_id,
                      value: epic.epic_id,
                      label: `[${epic.epic_id}] ${epic.epic_name}`,
                    }))}
                    components={{
                      option: CustomOption,
                      innerDropdown: () => CustomInnerDropdown({ name: project.project_name, id: project.project_id }),
                    }}
                    onChange={handleUpdate}
                    closeOnClick={false}
                    isSearchable={project.epics.length > 5}
                  />
                ))}
              </>
            )}
          </>
        ) : (
          <>
            <p>Connect Jira to use this source in your update.</p>
            <IntegrationsWrapper filters={{ showOnly: [IntegrationSources.JIRA] }} redirectUrl={oauthRedirectUrl} />
          </>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton onClick={toggleIsOpen}>Done</WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default JiraSourceModal;
